// PinPad — full-screen modal that asks the parent for the kid's
// exit PIN before signing out of Kids Mode. Big number buttons,
// minimum tap target 72px, no fancy chrome. The kid sees this if
// they tap the door icon and a PIN is set; entering the right PIN
// dismisses Kids Mode (signs out and lets a parent take over).
//
// Calls public.check_kid_exit_pin(p_pin) — security definer RPC
// that compares server-side. The hash never reaches this component.
//
// EMOJI-IN-UI EXCEPTION: this tree (src/experiments/kids-mode/**)
// is the explicit opt-in to emojis per the plan.

import { useEffect, useState } from "react";
import { supabase } from "../../lib/supabase";
import { COLOR, FONT, RADIUS } from "../../lib/tokens";

export function PinPad({ kidId, onCancel, onSuccess }) {
  const [pin, setPin] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  // Auto-submit at 4 digits; bigger PINs need a manual submit
  // (we allow 4-8 per the migration but most parents pick 4).
  useEffect(() => {
    if (pin.length === 4) submit(pin);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin]);

  const submit = async (value) => {
    if (!value || value.length < 4) return;
    if (!kidId) { setError("missing kid context"); return; }
    setBusy(true);
    setError(null);
    // Migration 0226: check_kid_exit_pin takes (p_kid_id, p_pin).
    const { data, error } = await supabase.rpc("check_kid_exit_pin", {
      p_kid_id: kidId,
      p_pin:    value,
    });
    setBusy(false);
    if (error) {
      setError("could not check pin");
      setPin("");
      return;
    }
    if (data === true) {
      onSuccess();
    } else {
      setError("wrong pin");
      setPin("");
    }
  };

  const press = (d) => {
    if (busy) return;
    setError(null);
    setPin((p) => (p.length < 8 ? p + d : p));
  };
  const back = () => {
    if (busy) return;
    setError(null);
    setPin((p) => p.slice(0, -1));
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(20,18,14,0.65)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        // Above ResultOverlay (zIndex: 300 in Craft.jsx) and the
        // KidParentPanel (zIndex: 240) so PinPad always sits on top
        // of whatever surface invoked it. The generation lockscreen
        // (zIndex: 9999) still wins after PIN success, which is the
        // correct order: PIN → generating-lock → finished.
        zIndex: 400,
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 360,
          background: "#FAF6EE",
          borderRadius: RADIUS.sheet,
          padding: 24,
          boxShadow: "0 12px 40px rgba(0,0,0,0.35)",
          textAlign: "center",
        }}
      >
        <div style={{ fontSize: 48, lineHeight: 1, marginBottom: 8 }}>🔒</div>
        <div
          style={{
            fontFamily: FONT.itemTitle,
            fontStyle: "italic",
            fontSize: 22,
            color: COLOR.ink,
            marginBottom: 6,
          }}
        >
          Grown-up only
        </div>
        <div
          style={{
            fontFamily: FONT.forma,
            fontSize: 13,
            color: COLOR.muted,
            marginBottom: 20,
          }}
        >
          Enter the PIN to exit
        </div>

        <Dots length={4} filled={Math.min(pin.length, 8)} />

        {error && (
          <div
            style={{
              marginTop: 12,
              fontFamily: FONT.forma,
              fontSize: 13,
              color: COLOR.rose,
              fontWeight: 600,
            }}
          >
            {error}
          </div>
        )}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 12,
            marginTop: 22,
          }}
        >
          {["1","2","3","4","5","6","7","8","9"].map((d) => (
            <PinKey key={d} digit={d} onPress={press} />
          ))}
          <div />
          <PinKey digit="0" onPress={press} />
          <PinKey digit="⌫" onPress={back} isBack />
        </div>

        <button
          type="button"
          onClick={onCancel}
          style={{
            marginTop: 18,
            padding: "12px 24px",
            background: "transparent",
            border: `1px solid ${COLOR.border}`,
            borderRadius: 10,
            fontFamily: FONT.mono,
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: "0.08em",
            color: COLOR.muted,
            cursor: "pointer",
          }}
        >
          CANCEL
        </button>
      </div>
    </div>
  );
}

function Dots({ length, filled }) {
  return (
    <div style={{ display: "flex", justifyContent: "center", gap: 14 }}>
      {Array.from({ length }).map((_, i) => (
        <div
          key={i}
          style={{
            width: 18,
            height: 18,
            borderRadius: "50%",
            background: i < filled ? COLOR.ink : "transparent",
            border: `2px solid ${i < filled ? COLOR.ink : COLOR.border}`,
            transition: "background 120ms ease",
          }}
        />
      ))}
    </div>
  );
}

function PinKey({ digit, onPress, isBack = false }) {
  return (
    <button
      type="button"
      onClick={() => onPress(digit)}
      className="mcm-focusable"
      style={{
        all: "unset",
        cursor: "pointer",
        height: 64,
        background: isBack ? "transparent" : "#fff",
        border: `1px solid ${COLOR.border}`,
        borderRadius: 14,
        fontFamily: FONT.forma,
        fontSize: 28,
        fontWeight: 500,
        color: COLOR.ink,
        textAlign: "center",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        touchAction: "manipulation",
      }}
    >
      {digit}
    </button>
  );
}

export default PinPad;
